import { FC } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { DepartmentsApi, DepartmentsTypes } from '@frontend/api-departments';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { FieldProps, useForm, useAlert } from '@frontend/design-system';
import { queryKeys } from '../../query-keys';
import { EditableHeader, nameConfigBase } from '../editable-header';

interface EditDepartmentNameProps {
  department: DepartmentsTypes.DepartmentModel | undefined;
  isLoading?: boolean;
}

export const EditDepartmentName: FC<React.PropsWithChildren<EditDepartmentNameProps>> = ({ department, isLoading }) => {
  const alert = useAlert();
  const queryClient = useQueryClient();
  const { selectedLocationIds } = useAppScopeStore();
  const locationId = selectedLocationIds[0];

  const { t } = useTranslation('phone', { keyPrefix: 'departments' });

  const form = useForm({
    fields: {
      name: {
        type: 'text',
        value: department?.name,
      },
    },
  });

  const nameField = form.getFieldProps('name') as FieldProps<typeof nameConfigBase, 'name'>;

  const { mutate: updateDepartment } = useMutation(
    ({ payload }: { payload: Partial<DepartmentsTypes.UpdateDepartmentTypes['input']> }) => {
      if (!payload.department?.id) {
        throw new Error(t('No id passed to department PUT request'));
      }
      return DepartmentsApi.updateDepartment(payload);
    },
    {
      onSuccess: (data) => {
        alert.success(t('Department name saved successfully'));
        queryClient.invalidateQueries([locationId, ...queryKeys.department(data.department?.id ?? '')]);
        queryClient.invalidateQueries([locationId, ...queryKeys.listDepartmentsWithSchedules()]);
      },
      onError: () => {
        alert.success(t('Error in updating name'));
      },
    }
  );

  const handleSave = (name: string) => {
    const payload = {
      department: {
        ...department,
        name: name,
      },
    };
    updateDepartment({ payload });
  };

  return (
    <EditableHeader
      formField={nameField}
      isEditable={!department?.mainLine}
      isLoading={isLoading}
      label={t('Department')}
      onSave={handleSave}
    />
  );
};
